/**
 * NovaLiveRoomCoHost — Nova as a PINNED participant at the top of every Live Room.
 * Sets the tone before anyone says anything.
 */

import React from 'react';
import NovaAvatarImg from './NovaAvatarImg';
import { NOVA_VOICE } from '../data/novaCommunityConfig';

const CHAPTER_THEMES = {
  fertility: { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
  ttc: { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
  pregnancy: { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
  motherhood: { accent: '#9B2942', stripBg: 'linear-gradient(140deg, #FBEAF0, #F5EAED)', accentBg: '#F5EAED' },
  postpartum: { accent: '#9B2942', stripBg: 'linear-gradient(140deg, #FBEAF0, #F5EAED)', accentBg: '#F5EAED' },
  menopause: { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
  cycle: { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
  'first-chapter': { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
};

export function NovaLiveRoomCoHost({
  theme,
  chapter = 'fertility',
  roomName,
  spiralRisk = 'low',
  onOpenNova,
}) {
  const voice = NOVA_VOICE[chapter] || NOVA_VOICE.fertility;
  const opening =
    voice.openingByRoom?.[roomName] ??
    "I'm here as your guide in this room. Ask me anything — I'll add context when it's useful.";

  const isHighRisk = spiralRisk === 'high';
  const t = theme || CHAPTER_THEMES[chapter] || CHAPTER_THEMES.fertility;

  return (
    <div
      style={{
        borderBottom: '1px solid var(--border, #eee)',
        padding: '12px 16px',
        background: isHighRisk ? t.stripBg : 'var(--card, #fff)',
      }}
    >
      {/* Pinned indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: 'rgba(60,30,45,0.45)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          📌 Pinned
        </span>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {/* Nova avatar with live indicator */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <NovaAvatarImg size={36} alt="" />
          <div
            style={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: '#1D9E75',
              border: '2px solid white',
            }}
          />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: t.accent }}>Nova</span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                padding: '2px 8px',
                borderRadius: 999,
                background: t.accentBg,
                color: t.accent,
                border: '0.5px solid rgba(212,83,126,0.2)',
              }}
            >
              {voice.communityTitle}
            </span>
          </div>

          <p style={{ fontSize: 13, color: '#2d1a22', lineHeight: 1.55 }}>{opening}</p>

          {/* High-risk rooms get an explicit direct offer */}
          {isHighRisk && (
            <p style={{ fontSize: 13, fontWeight: 500, color: t.accent, marginTop: 8 }}>
              Whatever the result — type anything and I'll respond.
            </p>
          )}

          <button
            type="button"
            onClick={onOpenNova}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: 11,
              fontWeight: 500,
              color: t.accent,
              cursor: 'pointer',
              marginTop: 8,
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
          >
            Ask Nova directly →
          </button>
        </div>
      </div>
    </div>
  );
}

export default NovaLiveRoomCoHost;
